import {
	Alert,
	Button,
	Group,
	Loader,
	Paper,
	Select,
	SimpleGrid,
	Stack,
	Table,
	Text,
	TextInput,
	Title,
} from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
	getReferralStats,
	referralKeys,
} from "@/features/referral/api/admin-referral";
import type {
	ReferralPeriod,
	ReferralStatsQuery,
} from "@/features/referral/api/types";
import { describeApiErrorSync } from "@/shared/api/error-message";

export function ReferralStatsPage() {
	const { t } = useTranslation();
	const [period, setPeriod] = useState<ReferralPeriod>("MONTH");
	const [from, setFrom] = useState("");
	const [to, setTo] = useState("");

	const customReady = period !== "CUSTOM" || (Boolean(from) && Boolean(to));

	const query: ReferralStatsQuery = useMemo(
		() => ({
			period,
			from: period === "CUSTOM" && from ? from : undefined,
			to: period === "CUSTOM" && to ? to : undefined,
		}),
		[period, from, to],
	);

	const statsQuery = useQuery({
		queryKey: referralKeys.stats(query),
		queryFn: () => getReferralStats(query),
		enabled: customReady,
	});

	const stats = statsQuery.data;

	return (
		<Stack gap="md">
			<div>
				<Title order={2}>{t("referral.statsTitle")}</Title>
				<Text c="dimmed" size="sm">
					{t("referral.statsSubtitle")}
				</Text>
			</div>

			<Group grow preventGrowOverflow={false} wrap="wrap" maw={720}>
				<Select
					label={t("referral.period")}
					data={[
						{ value: "ALL", label: t("referral.periodAll") },
						{ value: "WEEK", label: t("referral.periodWeek") },
						{ value: "MONTH", label: t("referral.periodMonth") },
						{ value: "CUSTOM", label: t("referral.periodCustom") },
					]}
					value={period}
					onChange={(value) => {
						if (
							value === "ALL" ||
							value === "WEEK" ||
							value === "MONTH" ||
							value === "CUSTOM"
						) {
							setPeriod(value);
						}
					}}
				/>
				{period === "CUSTOM" ? (
					<>
						<TextInput
							label={t("referral.from")}
							type="date"
							value={from}
							onChange={(e) => setFrom(e.currentTarget.value)}
						/>
						<TextInput
							label={t("referral.to")}
							type="date"
							value={to}
							onChange={(e) => setTo(e.currentTarget.value)}
						/>
					</>
				) : null}
			</Group>

			{period === "CUSTOM" && !customReady ? (
				<Text c="dimmed">{t("referral.customRangeHint")}</Text>
			) : null}

			{statsQuery.isLoading ? <Loader /> : null}

			{statsQuery.error ? (
				<Alert color="red" title={t("referral.statsLoadError")}>
					<Text size="sm">{describeApiErrorSync(statsQuery.error)}</Text>
					<Button
						variant="light"
						mt="sm"
						onClick={() => void statsQuery.refetch()}
					>
						{t("common.retry")}
					</Button>
				</Alert>
			) : null}

			{stats ? (
				<>
					<SimpleGrid cols={{ base: 1, sm: 2, md: 4 }}>
						<Kpi
							label={t("referral.kpiReferrals")}
							value={stats.successfulReferrals}
						/>
						<Kpi label={t("referral.kpiGranted")} value={stats.pointsGranted} />
						<Kpi
							label={t("referral.kpiRedeemed")}
							value={stats.pointsRedeemed}
						/>
						<Kpi
							label={t("referral.kpiCirculation")}
							value={stats.pointsInCirculation}
							hint={t("referral.kpiCirculationHint")}
						/>
					</SimpleGrid>

					<Paper p="md" radius="lg" withBorder>
						<Title order={4} mb="sm">
							{t("referral.redemptionsTitle")}
						</Title>
						{stats.redemptionsByItem.length === 0 ? (
							<Text c="dimmed">{t("referral.noRedemptions")}</Text>
						) : (
							<Table.ScrollContainer type="native" minWidth={480}>
								<Table verticalSpacing="sm">
									<Table.Thead>
										<Table.Tr>
											<Table.Th>{t("referral.item")}</Table.Th>
											<Table.Th>{t("referral.count")}</Table.Th>
											<Table.Th>{t("referral.pointsSpent")}</Table.Th>
										</Table.Tr>
									</Table.Thead>
									<Table.Tbody>
										{stats.redemptionsByItem.map((row) => (
											<Table.Tr key={row.item}>
												<Table.Td>{row.item}</Table.Td>
												<Table.Td>{row.count}</Table.Td>
												<Table.Td>{row.pointsSpent}</Table.Td>
											</Table.Tr>
										))}
									</Table.Tbody>
								</Table>
							</Table.ScrollContainer>
						)}
					</Paper>
				</>
			) : null}
		</Stack>
	);
}

function Kpi({
	label,
	value,
	hint,
}: {
	label: string;
	value: number;
	hint?: string;
}) {
	return (
		<Paper p="md" radius="lg" withBorder>
			<Text size="sm" c="dimmed">
				{label}
			</Text>
			<Title order={2}>{value}</Title>
			{hint ? (
				<Text size="xs" c="dimmed">
					{hint}
				</Text>
			) : null}
		</Paper>
	);
}
